/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { RadioGroup, RadioGroupDirection, RadioButton, Box } from '@base-app';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { TimeRangesProps } from './TimeRanges.types';
import { FormattedTimeRange, TimeRange } from '../../types';
import { StyledMessage, StyledLink, radioGroupStyles } from './TimeRanges.styles';
import { useTranslation } from 'react-i18next';
import { GENETIC_COUNSELING_TRANSLATION } from '../../constants';
import { HELIX_SUPPORT_EMAIL } from '@/common/constants';
import { getAppointmentTime, getTimeZoneString } from '../../utils';

const TimeRanges: FC<TimeRangesProps> = ({
  handleSelectedTimeRange,
  isErrorFetchingTimes,
  timeRangesData,
  timezone,
}) => {
  const { t } = useTranslation(GENETIC_COUNSELING_TRANSLATION);
  const [formattedTimeRanges, setFormattedTimeRanges] = useState<FormattedTimeRange[] | undefined>();

  const sortAndFormatTimeRanges = (timeRanges: TimeRange[]): void => {
    timeRanges.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    const formattedTimeRangesList: FormattedTimeRange[] = [];
    timeRanges.forEach((timeRange) => {
      formattedTimeRangesList.push({
        formattedTimeRange: getAppointmentTime(timeRange.startTime, timeRange.endTime, timezone, false) ?? '-',
        ...timeRange,
      });
    });
    setFormattedTimeRanges(formattedTimeRangesList);
  };

  useEffect(() => {
    if (timeRangesData.availableDatetimes != null && timeRangesData.availableDatetimes.length > 0) {
      sortAndFormatTimeRanges(timeRangesData.availableDatetimes);
    }
  }, [timeRangesData, timezone]);

  const handleTimeRangeSelected = (event: ChangeEvent<HTMLInputElement>): void => {
    const timeRange = timeRangesData.availableDatetimes.find((dateTime) => dateTime.startTime === event.target.value);
    if (timeRange !== undefined) handleSelectedTimeRange(timeRange);
  };
  return (
    <>
      {isErrorFetchingTimes && (
        <>
          <StyledMessage css={{ marginBottom: '$8' }}>{t('scheduler.times_error_message')}</StyledMessage>
          <Box css={{ display: 'flex', flexDirection: 'row' }}>
            <StyledMessage css={{ marginRight: '$1' }}>
              {t('scheduler.dates_error_notification.description')}
            </StyledMessage>
            <StyledLink href={`mailto:${HELIX_SUPPORT_EMAIL}`}>{HELIX_SUPPORT_EMAIL}</StyledLink>
          </Box>
        </>
      )}
      {!isErrorFetchingTimes &&
        (timeRangesData.availableDatetimes == null || timeRangesData.availableDatetimes.length === 0) && (
          <StyledMessage css={{ marginBottom: '$8' }}>{t('scheduler.times_none_available')}</StyledMessage>
        )}
      {!isErrorFetchingTimes &&
        timeRangesData.availableDatetimes != null &&
        timeRangesData.availableDatetimes.length > 0 && (
          <>
            <StyledMessage data-testid="time-ranges-timezone">
              {t('scheduler.available_appointment_times', {
                timezone:
                  timeRangesData.availableDatetimes &&
                  `(${getTimeZoneString(timeRangesData.availableDatetimes[0].startTime, timezone)})`,
              })}
            </StyledMessage>
            <RadioGroup
              css={radioGroupStyles}
              direction={RadioGroupDirection.ROWS}
              id="scheduler-time-range"
              name="scheduler-time-range"
              onChange={handleTimeRangeSelected}
            >
              {formattedTimeRanges !== undefined &&
                formattedTimeRanges.length > 0 &&
                formattedTimeRanges.map((timeRange, index) => (
                  <RadioButton
                    id={`time-range-number-${index}`}
                    key={index}
                    name={`time-range-number-${index}`}
                    value={timeRange.startTime}
                  >
                    {timeRange.formattedTimeRange}
                  </RadioButton>
                ))}
            </RadioGroup>
          </>
        )}
    </>
  );
};

export default TimeRanges;
